import React, { useState, useEffect } from 'react';

export default function Settings({ onClose }) {
  const [masterVolume, setMasterVolume] = useState(100);
  const [musicVolume, setMusicVolume] = useState(50);
  const [effectsVolume, setEffectsVolume] = useState(50);
  const [isMuted, setIsMuted] = useState(false);
  const [textSpeed, setTextSpeed] = useState(100);
  const [typeText, setTypeText] = useState(true);
  const [activeTab, setActiveTab] = useState('audio');

  useEffect(() => {
    setMasterVolume(window.gameAudio.savedVolumes.master * 100);
    setMusicVolume(window.gameAudio.savedVolumes.music * 100);
    setEffectsVolume(window.gameAudio.savedVolumes.effects * 100);
    setIsMuted(window.gameAudio.isMuted);
    setTextSpeed(window.gameLogic.getTypingSpeed());
    setTypeText(window.gameLogic.getToTypeText());
  }, []);

  const handleMasterVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setMasterVolume(value);
    window.gameAudio.setMasterVolume(value / 100);
  };

  const handleMusicVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setMusicVolume(value);
    window.gameAudio.setMusicVolume(value / 100);
  };

  const handleEffectsVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setEffectsVolume(value);
    window.gameAudio.setEffectsVolume(value / 100);
  };

  const handleMuteChange = (e) => {
    const muted = e.target.checked;
    setIsMuted(muted);
    window.gameAudio.setMute(muted);
  };

  const handleTextSpeedChange = (e) => {
    const value = parseInt(e.target.value);
    setTextSpeed(value);
    window.gameLogic.setTypingSpeed(value);
  };

  const handleTypeTextChange = (e) => {
    const value = e.target.checked;
    setTypeText(value);
    window.gameLogic.setToTypeText(value);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-none" />
      
      <div className="relative w-[800px] h-[600px] bg-gradient-to-br from-[#0c1220]/90 to-[#1a1b2e]/90 rounded-xl border border-[#ffffff15] shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#4a9eff30] to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#4a9eff30] to-transparent" />
          <div className="absolute top-10 right-10 w-32 h-32 bg-[#4a9eff20] rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-32 h-32 bg-[#4a9eff20] rounded-full blur-3xl" />
        </div>

        {/* Content */}
        <div className="relative p-8 h-full">
          <h2 className="font-['Orbitron'] text-3xl text-[#4a9eff] font-bold mb-2">
            System Settings
          </h2>
          <div className="w-32 h-0.5 bg-gradient-to-r from-[#4a9eff50] to-transparent mb-8" />

          {/* Tabs */}
          <div className="flex space-x-4 mb-8">
            {['audio', 'game'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2 rounded font-['Orbitron'] uppercase tracking-wider text-sm transition-all ${
                  activeTab === tab
                    ? 'bg-[#4a9eff20] text-[#4a9eff] border border-[#4a9eff40]'
                    : 'text-[#4a9eff80] hover:text-[#4a9eff] hover:bg-[#4a9eff10]'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Settings Content */}
          <div className="space-y-8">
            {activeTab === 'audio' && (
              <div className="space-y-6">
                {[
                  { label: 'Master Volume', value: masterVolume, onChange: handleMasterVolumeChange },
                  { label: 'Music Volume', value: musicVolume, onChange: handleMusicVolumeChange },
                  { label: 'Effects Volume', value: effectsVolume, onChange: handleEffectsVolumeChange }
                ].map(({ label, value, onChange }) => (
                  <div key={label} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-['Orbitron'] text-[#4a9eff]">{label}</span>
                      <span className="text-[#4a9eff80]">{value}%</span>
                    </div>
                    <input
                      type="range"
                      value={value}
                      onChange={onChange}
                      min="0"
                      max="100"
                      className="w-full appearance-none bg-[#4a9eff20] h-2 rounded-full"
                    />
                  </div>
                ))}
                
                <div className="flex items-center justify-between">
                  <span className="font-['Orbitron'] text-[#4a9eff]">Mute All</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMuted}
                      onChange={handleMuteChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-[#4a9eff20] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-[#4a9eff] after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4a9eff40]" />
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'game' && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-['Orbitron'] text-[#4a9eff]">Text Speed</span>
                    <span className="text-[#4a9eff80]">{textSpeed}%</span>
                  </div>
                  <input
                    type="range"
                    value={textSpeed}
                    onChange={handleTextSpeedChange}
                    min="1"
                    max="200"
                    className="w-full appearance-none bg-[#4a9eff20] h-2 rounded-full"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="font-['Orbitron'] text-[#4a9eff]">Type Text</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={typeText}
                      onChange={handleTypeTextChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-[#4a9eff20] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-[#4a9eff] after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4a9eff40]" />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-[#4a9eff80] hover:text-[#4a9eff] transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <style jsx>{`
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }

        input[type="range"]::-webkit-slider-runnable-track {
          background: linear-gradient(to right, #4a9eff40, #4a9eff20);
          border-radius: 0.25rem;
          height: 0.5rem;
        }

        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          margin-top: -4px;
          background-color: #4a9eff;
          border-radius: 50%;
          height: 1rem;
          width: 1rem;
          box-shadow: 0 0 10px #4a9eff80;
        }

        input[type="range"]:focus {
          outline: none;
        }

        input[type="range"]:focus::-webkit-slider-thumb {
          box-shadow: 0 0 15px #4a9eff;
        }
      `}</style>
    </div>
  );
}
